import React, { useMemo } from 'react';
import SanityImage from 'gatsby-plugin-sanity-image';
import { Link as GatsbyLink } from 'gatsby';
import { Flex, Text, Box, Link, usePrefersReducedMotion } from '@chakra-ui/react';

const MemoizedSanityImage = React.memo(SanityImage);

const ImageContent = React.memo(({ image, aspectRatio }) => (
  <MemoizedSanityImage
    {...image}
    alt={image?.asset?.altText}
    width={275}
    height={Math.round(275 / aspectRatio)}
    hotspot={image?.hotspot}
    crop={image?.crop}
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'top',
    }}
  />
));

const CardText = React.memo(({ title, excerpt, hideExcerpt, prefersReducedMotion }) => (
  <Flex
    direction='column'
    alignItems='start'
    justifyContent='center'
    gap='5px'
    width='100%'
    px={20}
    paddingTop={60}
    paddingBottom={20}
    transition='transform 0.4s ease-in-out'
    _groupHover={{
      transform: !prefersReducedMotion ? 'translateY(-10px)' : 'translateY(0)',
    }}
  >
    <Text width='100%' fontSize='xl' fontWeight='bold' color='inherit' lineHeight='1'>
      {title}
    </Text>
    {excerpt && !hideExcerpt && (
      <Text
        width='100%'
        fontSize='md'
        fontWeight='normal'
        color='inherit'
        lineHeight='base'
        sx={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {excerpt}
      </Text>
    )}
  </Flex>
));

function ImageCard({ data, hideExcerpt }) {
  const { section, aspectRatio } = data;
  const { section: sectionArea, slug, title, image, excerpt } = section;
  const prefersReducedMotion = usePrefersReducedMotion();

  const linkProps = useMemo(
    () => ({
      to: `/${sectionArea === 'single' ? '' : `${sectionArea}/`}${slug.current}`,
      as: GatsbyLink,
      color: 'white',
      _hover: {
        textDecoration: 'none',
        backgroundColor: prefersReducedMotion ? 'lightBlue' : 'zinc.100',
      },
      role: 'group',
      width: '100%',
      'aria-label': title,
      padding: 20,
      backgroundColor: 'zinc.100',
      flex: '1',
      minHeight: { base: '250px', lg: 'none' },
      display: 'block',
      transition: 'background-color 0.25s ease-in-out',
    }),
    [sectionArea, slug.current, title, prefersReducedMotion],
  );

  const imageContentProps = useMemo(
    () => ({
      image,
      aspectRatio,
    }),
    [image, aspectRatio],
  );

  const cardTextProps = useMemo(
    () => ({
      title,
      excerpt,
      hideExcerpt,
      prefersReducedMotion,
    }),
    [title, excerpt, hideExcerpt, prefersReducedMotion],
  );

  return (
    <Link {...linkProps}>
      <Flex
        direction='column'
        justifyContent='center'
        alignItems='center'
        gap={4}
        position='relative'
        height='100%'
        minHeight={{ base: '250px', lg: 'none' }}
        overflow='hidden'
      >
        <Box
          as='figure'
          height='100%'
          width='100%'
          position='absolute'
          transition='transform 0.4s ease-in-out'
          _groupHover={{
            transform: !prefersReducedMotion ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          {image && <ImageContent {...imageContentProps} />}
        </Box>
        <Flex
          direction='column'
          justifyContent='flex-end'
          alignItems='start'
          gap={2.5}
          width='100%'
          height='100%'
          zIndex={1}
          marginTop='auto'
          bgGradient='linear(to-b, transparent, black)'
        >
          <CardText {...cardTextProps} />
        </Flex>
      </Flex>
    </Link>
  );
}

export default React.memo(ImageCard);
